import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../../components/SearchableSelect';
import ExportModal from '../../components/ExportModal';
import { Button, ConfirmModal } from '../../components/ui';
import MemberLedgerModal from './MemberLedgerModal';
import {
  buildMarkPaidPayload,
  getDisplayBalanceClass,
  toDisplayBalance,
  type LedgerCharge,
  type LedgerPayment,
  type MemberBalance,
  type PaymentItem,
} from './paymentLedger';
import './PaymentOverviewPage.css';
import { translateError } from '../../utils/errorMessages';

// =============================================================================
// TYPES
// =============================================================================

interface PaymentsListResponse {
  payments: PaymentItem[];
  pagination?: { page: number; limit: number; total: number; pages: number };
}

interface PaymentBalanceResponse {
  dateRange: { start: string; end: string };
  summary: {
    totalOutstanding: number;
    totalCollected: number;
    membersWithBalance: number;
  };
  members: MemberBalance[];
}

interface GroupOption {
  _id: string;
  name: string;
}

interface MemberOption {
  _id: string;
  name: string;
}

// =============================================================================
// HELPERS
// =============================================================================

// The list route's validator caps limit at 100; the page cap is a safety belt.
const PAYMENTS_PAGE_SIZE = 100;
const MAX_PAYMENT_PAGES = 10;

function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0];
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

// =============================================================================
// COMPONENT
// =============================================================================

const PaymentOverviewPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();

  // Filters
  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const [startDate, setStartDate] = useState(formatDateForInput(yearStart));
  const [endDate, setEndDate] = useState('');
  // Deep link from the attendance_declined notification:
  // /admin/payments/overview?declined=1&memberId=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  const [groupFilter, setGroupFilter] = useState('');
  const [memberFilter, setMemberFilter] = useState(() => searchParams.get('memberId') ?? '');
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  // Attendance never cancels a charge; this surfaces declined-but-still-charged
  // event debts so the admin can decide (Cancel charge / keep).
  const [declinedOnly, setDeclinedOnly] = useState(() => searchParams.get('declined') === '1');

  // Data
  const [data, setData] = useState<PaymentBalanceResponse | null>(null);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  // The member whose ledger modal is open
  const [openMemberId, setOpenMemberId] = useState<string | null>(null);

  // Mark as paid state
  const [markingPaid, setMarkingPaid] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    memberId: string;
    memberName: string;
    amount: number;
    payload: Record<string, unknown>;
  } | null>(null);

  const canView = hasPermission('payments:read');
  const canWrite = hasPermission('payments:write');
  const canDelete = hasPermission('payments:delete');

  // Cancel charge state
  const [deletingIntent, setDeletingIntent] = useState<string | null>(null);
  const [deleteIntentTarget, setDeleteIntentTarget] = useState<string | null>(null);

  // Per-member payments, lazily fetched when the ledger modal opens
  const [memberPayments, setMemberPayments] = useState<Map<string, PaymentItem[]>>(new Map());
  const [paymentsLoading, setPaymentsLoading] = useState<Set<string>>(new Set());
  const [paymentsError, setPaymentsError] = useState<Set<string>>(new Set());
  const [paymentsTruncated, setPaymentsTruncated] = useState<Set<string>>(new Set());

  // Refund state
  const [refundTarget, setRefundTarget] = useState<{ payment: PaymentItem; memberId: string } | null>(null);
  const [refundLoading, setRefundLoading] = useState(false);

  // Delete payment state
  const [deletePaymentTarget, setDeletePaymentTarget] = useState<{ id: number; memberId: string } | null>(null);
  const [deletePaymentLoading, setDeletePaymentLoading] = useState(false);

  // Fetch filter options
  useEffect(() => {
    if (!canView) return;
    const fetchOptions = async () => {
      try {
        const [groupsRes, membersRes] = await Promise.all([
          api.groups.list(),
          api.members.list(),
        ]);
        setGroups(groupsRes.data as GroupOption[]);
        setMembers(membersRes.data as MemberOption[]);
      } catch {
        // Silently fail for filter options
      }
    };
    fetchOptions();
  }, [canView]);

  // Fetch balance data. Returns the response so a caller can tell whether the
  // member it was acting on survived the refresh.
  const fetchData = useCallback(async (): Promise<PaymentBalanceResponse | null> => {
    if (!canView) return null;
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (startDate) {
        params.startDate = new Date(startDate).toISOString();
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        params.endDate = end.toISOString();
      }
      if (groupFilter) params.groupId = groupFilter;
      if (memberFilter) params.memberId = memberFilter;

      const { data: result } = await api.payments.getBalances(params);
      setData(result as PaymentBalanceResponse);
      return result as PaymentBalanceResponse;
    } catch (err: any) {
      setError(translateError(err, t));
      return null;
    } finally {
      setLoading(false);
    }
  }, [canView, startDate, endDate, groupFilter, memberFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // The bell lives in the header, outside this route, so a second declined
  // notification navigates here while the page is already mounted and the
  // lazy useState initializers above would keep the previous member. Follow
  // the URL, then consume the params so a later toggle / refresh is honest.
  useEffect(() => {
    const declinedParam = searchParams.get('declined') === '1';
    const memberParam = searchParams.get('memberId');
    if (!declinedParam && !memberParam) return;
    if (declinedParam) setDeclinedOnly(true);
    if (memberParam) setMemberFilter(memberParam);
    const next = new URLSearchParams(searchParams);
    next.delete('declined');
    next.delete('memberId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Lazily fetch a member's payments for the ledger modal. The route caps limit
  // at 100, so page through rather than showing a silently truncated ledger.
  const fetchMemberPayments = useCallback(async (memberId: string) => {
    setPaymentsLoading(prev => new Set(prev).add(memberId));
    setPaymentsError(prev => {
      const next = new Set(prev);
      next.delete(memberId);
      return next;
    });
    try {
      const { data: first } = await api.payments.list({ member: memberId, limit: PAYMENTS_PAGE_SIZE });
      const firstPage = first as PaymentsListResponse;
      const rows = [...(firstPage.payments || [])];
      const pages = Math.min(firstPage.pagination?.pages ?? 1, MAX_PAYMENT_PAGES);
      if (pages > 1) {
        const rest = await Promise.all(
          Array.from({ length: pages - 1 }, (_, i) =>
            api.payments.list({ member: memberId, limit: PAYMENTS_PAGE_SIZE, page: i + 2 })),
        );
        for (const { data: page } of rest) {
          rows.push(...((page as PaymentsListResponse).payments || []));
        }
      }
      // Concurrent offset pages can repeat a row when the underlying set shifts.
      const seen = new Set<number>();
      const unique = rows.filter(r => !seen.has(r._id) && seen.add(r._id));
      setMemberPayments(prev => new Map(prev).set(memberId, unique));
      setPaymentsTruncated(prev => {
        const next = new Set(prev);
        if ((firstPage.pagination?.pages ?? 1) > MAX_PAYMENT_PAGES) next.add(memberId);
        else next.delete(memberId);
        return next;
      });
    } catch {
      setPaymentsError(prev => new Set(prev).add(memberId));
      setMemberPayments(prev => {
        const next = new Map(prev);
        next.delete(memberId);
        return next;
      });
    } finally {
      setPaymentsLoading(prev => {
        const next = new Set(prev);
        next.delete(memberId);
        return next;
      });
    }
  }, []);

  const openLedger = (memberId: string) => {
    setOpenMemberId(memberId);
    if (!memberPayments.has(memberId)) fetchMemberPayments(memberId);
  };

  // A cancelled last charge drops the member from the balances result; close the
  // modal rather than leaving an empty dialog on screen.
  const refreshBalances = useCallback(async (memberId: string) => {
    const refreshed = await fetchData();
    if (refreshed && !refreshed.members.some(m => m.memberId === memberId)) {
      setOpenMemberId(null);
    }
  }, [fetchData]);

  // Mark as paid handler
  const handleMarkAsPaid = async () => {
    if (!confirmDialog) return;

    const { memberId, payload } = confirmDialog;
    setMarkingPaid(true);

    try {
      await api.payments.create(payload);
      addToast(t('paymentOverview.paymentCreated'), 'success');
      setConfirmDialog(null);
      await Promise.all([refreshBalances(memberId), fetchMemberPayments(memberId)]);
    } catch {
      addToast(t('paymentOverview.paymentError'), 'error');
    } finally {
      setMarkingPaid(false);
    }
  };

  // Cancel charge handler — one-way and terminal
  const handleDeleteIntentConfirm = async () => {
    if (!deleteIntentTarget) return;
    setDeletingIntent(deleteIntentTarget);
    try {
      await api.payments.deleteIntent(deleteIntentTarget);
      addToast(t('payments.deleteIntentSuccess'), 'success');
      if (openMemberId) await refreshBalances(openMemberId);
      else await fetchData();
    } catch (err: any) {
      addToast(translateError(err, t, 'paymentOverview.paymentError'), 'error');
    } finally {
      setDeletingIntent(null);
      setDeleteIntentTarget(null);
    }
  };

  // Refund a payment (full amount), then refresh balances + the member's list
  const handleRefundConfirm = async () => {
    if (!refundTarget) return;
    const { payment, memberId } = refundTarget;
    setRefundLoading(true);
    try {
      await api.payments.refund(String(payment._id), { amount: Number(payment.amount) });
      addToast(t('payments.refundSuccess'), 'success');
      setRefundTarget(null);
      await Promise.all([refreshBalances(memberId), fetchMemberPayments(memberId)]);
    } catch (err: any) {
      addToast(translateError(err, t, 'payments.refundError'), 'error');
    } finally {
      setRefundLoading(false);
    }
  };

  // Delete a payment, then refresh balances + the member's list
  const handleDeletePaymentConfirm = async () => {
    if (!deletePaymentTarget) return;
    const { id, memberId } = deletePaymentTarget;
    setDeletePaymentLoading(true);
    try {
      await api.payments.delete(String(id));
      setDeletePaymentTarget(null);
      await Promise.all([refreshBalances(memberId), fetchMemberPayments(memberId)]);
    } catch (err: any) {
      addToast(translateError(err, t, 'paymentOverview.paymentError'), 'error');
    } finally {
      setDeletePaymentLoading(false);
    }
  };

  // Filter options for SearchableSelect
  const groupOptions = useMemo(
    () => groups.map(g => ({ _id: g._id, name: g.name })),
    [groups]
  );

  const memberOptions = useMemo(
    () => members.map(m => ({ _id: m._id, name: m.name })),
    [members]
  );

  // Unpaid-only filtering (applied after data fetch, doesn't affect summary cards)
  const filteredMembers = useMemo(() => {
    if (!data) return [];
    let list = data.members;
    if (unpaidOnly) list = list.filter(m => m.balance > 0);
    if (declinedOnly) {
      list = list.filter(m => m.eventDebts.some(d => d.attending === false && d.balance > 0));
    }
    return list;
  }, [data, unpaidOnly, declinedOnly]);

  // Off the unfiltered list: paying a member off must not yank the modal away
  // because they no longer match "unpaid only".
  const openMember = useMemo(
    () => data?.members.find(m => m.memberId === openMemberId) ?? null,
    [data, openMemberId]
  );

  // The open member can leave `data` for reasons no mutation handler sees — a
  // filter or date change. Close rather than leave an empty dialog.
  useEffect(() => {
    if (openMemberId && data && !openMember) setOpenMemberId(null);
  }, [openMemberId, data, openMember]);

  // Export config
  const exportFields = useMemo(() => [
    { key: 'memberName', label: t('paymentOverview.member'), category: 'attendance' as const, defaultChecked: true },
    { key: 'groups', label: t('paymentOverview.groups'), category: 'attendance' as const, defaultChecked: true },
    { key: 'totalOwed', label: t('paymentOverview.totalOwed'), category: 'attendance' as const, defaultChecked: true },
    { key: 'totalPaid', label: t('paymentOverview.totalPaid'), category: 'attendance' as const, defaultChecked: true },
    { key: 'balance', label: t('paymentOverview.balance'), category: 'attendance' as const, defaultChecked: true },
  ], [t]);

  // Balance is exported with the sign the screen shows, or the file contradicts it.
  const exportData = useMemo(() => {
    return filteredMembers.map(m => ({
      memberName: m.memberName,
      groups: m.groups.map(g => g.name).join(', '),
      totalOwed: m.totalOwed,
      totalPaid: m.totalPaid,
      balance: toDisplayBalance(m.balance),
    }));
  }, [filteredMembers]);

  if (!canView) {
    return (
      <div className="payment-overview">
        <h2>{t('paymentOverview.title')}</h2>
        <p className="error-message">{t('common.noPermission')}</p>
      </div>
    );
  }

  return (
    <div className="payment-overview">
      {/* Header */}
      <div className="payment-overview__header">
        <h2>{t('paymentOverview.title')}</h2>
        <div className="payment-overview__header-actions">
          <Button
            variant="secondary"
            style={{ background: 'var(--bg-light)' }}
            onClick={() => setShowExportModal(true)}
            disabled={loading || !data}
          >
            {t('export.exportButton')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="payment-overview__filters">
        <div className="payment-overview__filter-group">
          <label>{t('paymentOverview.startDate')}</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="payment-overview__filter-group">
          <label>{t('paymentOverview.endDate')}</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="payment-overview__filter-group">
          <label>{t('paymentOverview.filterByGroup')}</label>
          <SearchableSelect
            options={groupOptions}
            value={groupFilter || null}
            onChange={(val) => setGroupFilter(val == null ? '' : String(val))}
            placeholder={t('paymentOverview.allGroups')}
          />
        </div>
        <div className="payment-overview__filter-group">
          <label>{t('paymentOverview.filterByMember')}</label>
          <SearchableSelect
            options={memberOptions}
            value={memberFilter || null}
            onChange={(val) => setMemberFilter(val == null ? '' : String(val))}
            placeholder={t('paymentOverview.allMembers')}
          />
        </div>
        <div className="payment-overview__filter-group">
          <label>&nbsp;</label>
          <Button
            variant={unpaidOnly ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setUnpaidOnly(prev => !prev)}
          >
            {t('paymentOverview.unpaidOnly')}
          </Button>
        </div>
        <div className="payment-overview__filter-group">
          <label>&nbsp;</label>
          <Button
            variant={declinedOnly ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setDeclinedOnly(prev => !prev)}
          >
            {t('paymentOverview.declinedOnly')}
          </Button>
        </div>
      </div>

      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      {loading ? (
        <div className="payment-overview__loading">{t('paymentOverview.loading')}</div>
      ) : data && (
        <>
          {/* Summary Cards — unsigned magnitudes; the labels state direction */}
          <div className="payment-overview__summary">
            <div className="payment-overview__summary-card">
              <div className="payment-overview__summary-card-label">
                {t('paymentOverview.totalOutstanding')}
              </div>
              <div className="payment-overview__summary-card-value payment-overview__summary-card-value--danger">
                {formatCurrency(data.summary.totalOutstanding)}
              </div>
            </div>
            <div className="payment-overview__summary-card">
              <div className="payment-overview__summary-card-label">
                {t('paymentOverview.totalCollected')}
              </div>
              <div className="payment-overview__summary-card-value payment-overview__summary-card-value--success">
                {formatCurrency(data.summary.totalCollected)}
              </div>
            </div>
            <div className="payment-overview__summary-card">
              <div className="payment-overview__summary-card-label">
                {t('paymentOverview.membersWithBalance')}
              </div>
              <div className="payment-overview__summary-card-value">
                {data.summary.membersWithBalance}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="payment-overview__table-container">
            <table className="payment-overview__table">
              <thead>
                <tr>
                  <th className="payment-overview__col-member">{t('paymentOverview.member')}</th>
                  <th className="payment-overview__col-groups">{t('paymentOverview.groups')}</th>
                  <th>{t('paymentOverview.totalOwed')}</th>
                  <th>{t('paymentOverview.totalPaid')}</th>
                  <th>{t('paymentOverview.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="payment-overview__no-data">
                      {t('paymentOverview.noData')}
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((member) => {
                    const display = toDisplayBalance(member.balance);
                    return (
                      <tr
                        key={member.memberId}
                        className="payment-overview__row"
                        onClick={() => openLedger(member.memberId)}
                      >
                        <td className="payment-overview__col-member">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="payment-overview__member-btn"
                            aria-label={t('paymentOverview.openLedger', { member: member.memberName })}
                            onClick={(e) => {
                              e.stopPropagation();
                              openLedger(member.memberId);
                            }}
                          >
                            {member.memberName}
                          </Button>
                        </td>
                        <td className="payment-overview__col-groups">
                          {member.groups.map(g => g.name).join(', ') || '-'}
                        </td>
                        <td>{formatCurrency(member.totalOwed)}</td>
                        <td>{formatCurrency(member.totalPaid)}</td>
                        <td className={`payment-overview__col-balance ${getDisplayBalanceClass(display)}`}>
                          {formatCurrency(display)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Member ledger */}
      <MemberLedgerModal
        member={openMember}
        payments={openMemberId ? memberPayments.get(openMemberId) : undefined}
        paymentsLoading={!!openMemberId && paymentsLoading.has(openMemberId)}
        paymentsError={!!openMemberId && paymentsError.has(openMemberId)}
        paymentsTruncated={!!openMemberId && paymentsTruncated.has(openMemberId)}
        onRetryPayments={() => { if (openMemberId) fetchMemberPayments(openMemberId); }}
        canWrite={canWrite}
        canDelete={canDelete}
        onClose={() => setOpenMemberId(null)}
        onMarkPaid={(charge: LedgerCharge) => {
          if (!openMember) return;
          const payload = buildMarkPaidPayload(openMember.memberId, charge);
          if (!payload) return;
          setConfirmDialog({
            memberId: openMember.memberId,
            memberName: openMember.memberName,
            amount: charge.balance,
            payload,
          });
        }}
        onCancelCharge={(intentId: string) => setDeleteIntentTarget(intentId)}
        onRefund={(payment: LedgerPayment) => {
          if (!openMember) return;
          setRefundTarget({ payment: payment.source, memberId: openMember.memberId });
        }}
        onDeletePayment={(payment: LedgerPayment) => {
          if (!openMember) return;
          setDeletePaymentTarget({ id: payment.id, memberId: openMember.memberId });
        }}
      />

      {/* Mark as paid confirmation */}
      <ConfirmModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onConfirm={handleMarkAsPaid}
        title={t('paymentOverview.markAsPaidConfirm')}
        message={confirmDialog ? t('paymentOverview.markAsPaidConfirmDesc', {
          amount: formatCurrency(confirmDialog.amount),
          member: confirmDialog.memberName,
        }) : ''}
        confirmText={t('paymentOverview.confirm')}
        cancelText={t('paymentOverview.cancel')}
        loading={markingPaid}
      />

      {/* Refund payment confirmation */}
      <ConfirmModal
        isOpen={!!refundTarget}
        onClose={() => setRefundTarget(null)}
        onConfirm={handleRefundConfirm}
        title={t('payments.refund')}
        message={refundTarget
          ? `${t('payments.maxRefund')}: ${formatCurrency(Number(refundTarget.payment.amount))}`
          : ''}
        confirmText={t('payments.refundConfirm')}
        cancelText={t('common.cancel')}
        loading={refundLoading}
      />

      {/* Delete payment confirmation */}
      <ConfirmModal
        isOpen={!!deletePaymentTarget}
        onClose={() => setDeletePaymentTarget(null)}
        onConfirm={handleDeletePaymentConfirm}
        title={t('payments.deletePaymentTitle')}
        message={t('payments.deletePaymentConfirm')}
        confirmText={t('payments.deletePayment')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deletePaymentLoading}
      />

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={exportData}
        filename={`payment-overview-${startDate}${endDate ? `-${endDate}` : ''}`}
        availableFields={exportFields}
        title={t('paymentOverview.title')}
      />

      <ConfirmModal
        isOpen={!!deleteIntentTarget}
        onClose={() => setDeleteIntentTarget(null)}
        onConfirm={handleDeleteIntentConfirm}
        title={t('paymentOverview.cancelCharge')}
        message={t('payments.deleteIntentConfirm')}
        confirmText={t('paymentOverview.cancelCharge')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={!!deletingIntent}
      />
    </div>
  );
};

export default PaymentOverviewPage;
